import type { TFunction } from "i18next";
import { convertDocxToPdfBlob } from "./office-to-pdf";
import { convertImageLikeToPdfBlob, convertNonImageToPdfBlob } from "src/utils/docUploads/convert-image-to-pdf";

function ext(name: string) {
  const n = name.toLowerCase();
  const i = n.lastIndexOf(".");
  return i >= 0 ? n.slice(i) : "";
}

// These extensions are handled fully in the browser via our "Office -> PDF" path.
// (This is an allowlist)
const FRONTEND_OFFICE = new Set([".docx", ".xlsx", ".rtf"]);

type Converters = {
  convertFileToPdfBlob: typeof convertFileToPdfBlob;
  convertDocxToPdfBlob: typeof convertDocxToPdfBlob;
};

export async function convertAnyToPdfBlobFrontendOnly(
  file: File,
  t: TFunction,
  converters: Converters = { convertFileToPdfBlob, convertDocxToPdfBlob }
): Promise<Blob> {
  const mime = (file.type || "").toLowerCase();
  const e = ext(file.name);

  // Office documents (that we explicitly support in the frontend) go through the Office converter.
  // This avoids the "fallback PDF" behaviour for unsupported binaries.
  if (FRONTEND_OFFICE.has(e)) {
    return converters.convertDocxToPdfBlob(file, t);
  }

  // Everything else is an allowlist:
  // - PDFs: pass-through / wrapped as needed
  // - Images: embedded into a PDF
  // - HEIC/HEIF: some browsers report an empty or unexpected MIME type, so we also detect by signature
  const isAllowed = mime === "application/pdf" || mime.startsWith("image/") || isHeicLike(file) || e === ".docx"; // redundant with FRONTEND_OFFICE, kept for clarity in the allowlist

  // If the file isn't in our known-supported set, fail fast with a clear error message.
  if (!isAllowed) {
    throw new Error(t("pdfConversion.errors.unsupportedFileType", { ext: e || "unknown", mime: mime || "unknown" }));
  }

  // PDFs + images use the generic converter (image->pdf, pdf passthrough, etc.)
  return converters.convertFileToPdfBlob(file, t);
}

/**
 * Main entry point:
 * - PDFs: return as a PDF Blob (no conversion)
 * - Images/HEIC: render into a 1-page PDF
 * - Everything else: metadata-only PDF (+ optional attachment)
 */
export async function convertFileToPdfBlob(file: File, t: TFunction): Promise<Blob> {
  if (isPdf(file)) return convertPdfToBlob(file);
  if (isImage(file) || isHeicLike(file)) return convertImageLikeToPdfBlob(file, t);
  return convertNonImageToPdfBlob(file, t);
}

/**
 * HEIC/HEIF detection helper.
 * We check both MIME type and file extension because some browsers / upload components
 * do not reliably populate `file.type` for HEIC/HEIF.
 */
export function isHeicLike(file: File) {
  const name = file.name.toLowerCase();
  const t = (file.type || "").toLowerCase();
  return t === "image/heic" || t === "image/heif" || name.endsWith(".heic") || name.endsWith(".heif");
}

/**
 * Basic PDF detection.
 * We check both MIME type and extension because MIME type isn't always set consistently.
 */
function isPdf(file: File) {
  return (file.type || "").toLowerCase() === "application/pdf" || file.name.toLowerCase().endsWith(".pdf");
}

/**
 * "Normal" image detection (jpeg/png/webp/etc).
 * HEIC/HEIF is handled separately via `isHeicLike()`.
 */
function isImage(file: File) {
  return (file.type || "").toLowerCase().startsWith("image/");
}

/**
 * Replaces the original file extension with `.pdf`.
 * e.g. "photo.png" -> "photo.pdf"
 */
export function makePdfFileName(originalName: string, currentConvertedFileNames: string[]) {
  const base = originalName.replace(/\.[^/.]+$/, "");
  let current = base;
  let checkingDuplicateName = true;
  let versionCount = 1;
  while (checkingDuplicateName) {
    if (currentConvertedFileNames.includes(current)) {
      current = `${base}(${versionCount})`;
      versionCount += 1;
    } else {
      checkingDuplicateName = false;
    }
  }
  return `${current}.pdf`;
}


async function convertPdfToBlob(file: File): Promise<Blob> {
  const originalBytes = new Uint8Array(await file.arrayBuffer());
  return new Blob([originalBytes], { type: "application/pdf" });
}
